import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { AncDataService } from 'src/app/services/ANC/anc-data.service';
import { DeviceService } from 'src/app/services/devices/device.service';
import { getRelayOutputCount } from 'src/app/services/devices/device-type.util';
import { DlDataService } from 'src/app/services/DL/dl-data.service';
import { DlCycleRegion, DlProgram, DlPulseRegion, DlScheduleService } from 'src/app/services/DL/dl-schedule.service';
import { WebsocketService } from 'src/app/services/websocket/websocket.service';

@Component({
    selector: 'app-d2',
    imports: [CommonModule, TranslateModule],
    templateUrl: './d2.component.html'
})
export class D2Component implements OnInit, OnDestroy {

  private connectTimer?: ReturnType<typeof setTimeout>;

  @Input() serial: string = '';
  outputCount = 2;
  @ViewChild('programTodayOut1') editCanvasOut1!: ElementRef<HTMLCanvasElement>;
  @ViewChild('programTodayOut2') editCanvasOut2!: ElementRef<HTMLCanvasElement>;
  @ViewChild('overlayContainer') overlayContainer!: ElementRef<HTMLElement>;
  @ViewChild('nowLinesOverlay') nowLinesOverlay!: ElementRef<HTMLCanvasElement>;
  @ViewChild('pulseZoomOverlay') pulseZoomOverlay!: ElementRef<HTMLCanvasElement>;

  loading = true;
  deviceInfo: any;
  dlDeviceInfo: any;
  modelDLData: any;
  channelInfo: any;
  programDataFormated: DlProgram[] = [];
  clockTime: any;
  calendarStat: any;
  lastConnection = '';
  seasonsInfo: any = { enable: 0, winter: null, summer: null };
  holidayInfo: any = { enable: 0, periods: [] };
  todayPeriod: any = {};

  loadState = { c1: false, c2: false };
  loadingClockSync = false;
  warning = false;

  chartDataOut1: number[] = [];
  chartDataOut2: number[] = [];
  pulseRegionsOut1: DlPulseRegion[] = [];
  pulseRegionsOut2: DlPulseRegion[] = [];
  cycleRegionsOut1: DlCycleRegion[] = [];
  cycleRegionsOut2: DlCycleRegion[] = [];

  private chartOut1: Chart | null = null;
  private chartOut2: Chart | null = null;
  private nowLineTimerId: number | null = null;
  private deviceClockBaseDate: Date | null = null;
  private deviceClockCapturedAtMs: number | null = null;
  private deviceClockDriftMinutes: number | null = null;
  private hoveredSchedule:
    | { kind: 'pulse'; output: 1 | 2; region: DlPulseRegion }
    | { kind: 'cycle'; output: 1 | 2; region: DlCycleRegion }
    | null = null;

  private pulseIconImage: HTMLImageElement | null = null;
  private pulseIconDarkImage: HTMLImageElement | null = null;
  private cycleIconImage: HTMLImageElement | null = null;

  private readonly pulseHitPaddingMinutes = 6;
  private readonly cycleHitPaddingMinutes = 2;

  private readonly minutesPerDay = 1440;
  private readonly weekMinutes = this.minutesPerDay * 7;
  private readonly windowBackMinutes = 6 * 60;
  private readonly windowForwardMinutes = 24 * 60;
  private readonly nowSnapMinutes = 10;
  private readonly clockDriftWarningThresholdMinutes = 3;

  daysOfWeek = [
    { index: 0, label: 'Lunes', value: 64, short: 'L' },
    { index: 1, label: 'Martes', value: 32, short: 'M' },
    { index: 2, label: 'Miércoles', value: 16, short: 'X' },
    { index: 3, label: 'Jueves', value: 8, short: 'J' },
    { index: 4, label: 'Viernes', value: 4, short: 'V' },
    { index: 5, label: 'Sábado', value: 2, short: 'S' },
    { index: 6, label: 'Domingo', value: 1, short: 'D' }
  ];

  constructor(
    private _device: DeviceService,
    private _dlDataService: DlDataService,
    private _dlScheduleService: DlScheduleService,
    private _ancDataService: AncDataService,
    private _ws: WebsocketService,
    private translate: TranslateService,
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.chartOut1?.resize();
    this.chartOut2?.resize();
    window.setTimeout(() => {
      this.renderNowLinesOverlay();
      this.renderScheduleOverlay();
    }, 50);
  }

  ngOnInit(): void {
    this.outputCount = getRelayOutputCount(this.serial);
    this.preloadPulseIcon();
    this.preloadPulseIconDark();
    this.preloadCycleIcon();

    this.connectTimer = setTimeout(() => {
      this.loadData();
      this.connectWebsocket();
      this.loading = false;
    }, 500);
  }

  ngOnDestroy(): void {
    // Sin esto el timer de arranque conecta el websocket tras destruir la pantalla y le roba el socket a la siguiente.
    clearTimeout(this.connectTimer);
    if (this.nowLineTimerId != null) {
      window.clearInterval(this.nowLineTimerId);
      this.nowLineTimerId = null;
    }

    this._ws.close();
    this.chartOut1?.destroy();
    this.chartOut1 = null;
    this.chartOut2?.destroy();
    this.chartOut2 = null;
  }

  connectWebsocket(): void {
    this._ws.connect(this.serial).subscribe((message) => {
      if (!this.channelInfo?.stat || !message?.data?.stat) return;

      if (message.data.stat.c1stat !== undefined) {
        this.channelInfo.stat.c1stat = message.data.stat.c1stat;
      }
      if (message.data.stat.c2stat !== undefined) {
        this.channelInfo.stat.c2stat = message.data.stat.c2stat;
      }
      this.lastConnection = new Date().toISOString();
    });
  }

  get totalCounterHours(): number {
    const c1 = Number(this.channelInfo?.stat?.c1counter) || 0;
    if (this.outputCount < 2) return c1;
    return c1 + (Number(this.channelInfo?.stat?.c2counter) || 0);
  }

  loadData(): void {
    this.outputCount = getRelayOutputCount(this.serial);
    this._device.getDeviceInfo(this.serial).subscribe((data) => {
      this.deviceInfo = data;
      this.lastConnection = new Date().toISOString();
    });

    this._device.getModuleInfo(this.serial, 'device').subscribe((data) => {
      this.dlDeviceInfo = data.cfg;
      this.modelDLData = this._dlDataService.getModelData(data.cfg?.model);
    });

    this._device.getModuleInfo(this.serial, 'seasons').subscribe((data) => {
      this.seasonsInfo = this._ancDataService.formatSeasonData(data.cfg);
    });

    this._device.getModuleInfo(this.serial, 'holiday').subscribe((holidayInfo) => {
      this.holidayInfo = this._ancDataService.formatHolidayData(holidayInfo.cfg);
      this.todayPeriod = this._ancDataService.isTodayInAnyPeriod(this.holidayInfo?.periods);
      this.rebuildBothCharts();
    });

    this._device.getModuleInfo(this.serial, 'schedule').subscribe((data) => {
      this.programDataFormated = this._dlDataService.formatProgramData(data.cfg);
      setTimeout(() => this.rebuildBothCharts(), 300);
    });

    this._device.getModuleInfo(this.serial, 'channel').subscribe((channelInfo) => {
      this.channelInfo = channelInfo;
      this.lastConnection = new Date().toISOString();
    });

    this._device.getModuleInfo(this.serial, 'calendar', 'stat', { localdata: true }).subscribe((data) => {
      this.calendarStat = data.calendar;
      this.clockTime = this._dlDataService.formatCalendarStatus(this.calendarStat);
      this.syncDeviceClockReference(this.clockTime);
      this.validateDeviceClockAgainstSystem();
      this.lastConnection = new Date().toISOString();
      this.renderNowLinesOverlay();
    });
  }

  changeOutput(output: number): void {
    switch (output) {
      case 1:
        if (this.channelInfo?.stat?.c1stat === 2) this.channelInfo.stat.c1stat = 1;
        else if (this.channelInfo?.stat?.c1stat === 3) this.channelInfo.stat.c1stat = 0;
        else if (this.channelInfo?.stat?.c1stat === 0) this.channelInfo.stat.c1stat = 3;
        else this.channelInfo.stat.c1stat = 2;
        break;
      case 2:
        if (this.channelInfo?.stat?.c2stat === 2) this.channelInfo.stat.c2stat = 1;
        else if (this.channelInfo?.stat?.c2stat === 3) this.channelInfo.stat.c2stat = 0;
        else if (this.channelInfo?.stat?.c2stat === 0) this.channelInfo.stat.c2stat = 3;
        else this.channelInfo.stat.c2stat = 2;
        break;
    }

    if (output === 1) this.loadState.c1 = true;
    if (output === 2) this.loadState.c2 = true;

    const body = {
      c1stat: this.channelInfo?.stat?.c1stat,
      c2stat: this.channelInfo?.stat?.c2stat,
    };

    this._device.setModuleCmd(this.serial, body, 'channel', 'relay').subscribe(() => {
      this.lastConnection = new Date().toISOString();
      setTimeout(() => {
        this.loadState.c1 = false;
        this.loadState.c2 = false;
      }, 1000);
    });
  }

  resetCounter(counter: number): void {
    if (!window.confirm(this.t('dlResetChannelHoursConfirm', { channel: counter }))) return;

    const body: Record<string, number> = {};
    if (counter === 1) body.c1counter = 0;
    else body.c2counter = 0;

    this._device.setModuleCfg(this.serial, body, 'channel').subscribe(() => {
      if (counter === 1) this.channelInfo.stat.c1counter = 0;
      else this.channelInfo.stat.c2counter = 0;
    });
  }

  syncClock(): void {
    if (this.loadingClockSync) return;
    this.loadingClockSync = true;

    const now = new Date();
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let weekday = 0;
    if (date.getDay() === 0) weekday = 1;
    if (date.getDay() === 1) weekday = 64;
    if (date.getDay() === 2) weekday = 32;
    if (date.getDay() === 3) weekday = 16;
    if (date.getDay() === 4) weekday = 8;
    if (date.getDay() === 5) weekday = 4;
    if (date.getDay() === 6) weekday = 2;

    const yearOffset = Math.max(0, now.getFullYear() - 2000);
    const body: { calendar: number[] } = {
      calendar: [
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        weekday,
        now.getDate(),
        now.getMonth() + 1,
        yearOffset,
      ],
    };

    this._device.setModuleCfg(this.serial, body, 'calendar').subscribe({
      next: () => {
        this.refreshDeviceClock();
        this.loadingClockSync = false;
      },
      error: () => {
        this.loadingClockSync = false;
      },
    });
  }

  private refreshDeviceClock(): void {
    this._device.getModuleInfo(this.serial, 'calendar', 'stat', { localdata: true }).subscribe((data) => {
      this.calendarStat = data.calendar;
      this.clockTime = this._dlDataService.formatCalendarStatus(this.calendarStat);
      this.syncDeviceClockReference(this.clockTime);
      this.validateDeviceClockAgainstSystem();
      this.lastConnection = new Date().toISOString();
      this.renderNowLinesOverlay();
    });
  }

  get clockDriftWarningTitle(): string {
    if (this.deviceClockDriftMinutes == null) {
      return this.t('dlClockNotSynced');
    }
    const minutes = Math.abs(this.deviceClockDriftMinutes);
    const direction = this.t(this.deviceClockDriftMinutes > 0 ? 'dlClockDriftAhead' : 'dlClockDriftBehind');
    return this.t('dlClockDrift', { minutes, direction });
  }

  private validateDeviceClockAgainstSystem(): void {
    if (!this.deviceClockBaseDate || this.deviceClockCapturedAtMs == null) {
      this.deviceClockDriftMinutes = null;
      this.warning = false;
      return;
    }

    const deviceNow = this.getReferenceNow();
    const systemNow = new Date();
    const driftMinutes = Math.round((deviceNow.getTime() - systemNow.getTime()) / 60000);
    this.deviceClockDriftMinutes = driftMinutes;
    this.warning = Math.abs(driftMinutes) > this.clockDriftWarningThresholdMinutes;
  }

  private rebuildBothCharts(): void {
    const activeFestive = this.todayPeriod?.periodIndex ?? 0;
    this.chartDataOut1 = this._dlScheduleService.buildWeekChartData(this.programDataFormated, activeFestive, 1);
    this.chartDataOut2 = this._dlScheduleService.buildWeekChartData(this.programDataFormated, activeFestive, 2);
    this.pulseRegionsOut1 = this._dlScheduleService.getPulseRegions(this.programDataFormated, activeFestive, 1);
    this.pulseRegionsOut2 = this._dlScheduleService.getPulseRegions(this.programDataFormated, activeFestive, 2);
    this.cycleRegionsOut1 = this._dlScheduleService.getCycleRegions(this.programDataFormated, activeFestive, 1);
    this.cycleRegionsOut2 = this._dlScheduleService.getCycleRegions(this.programDataFormated, activeFestive, 2);
    this.hoveredSchedule = null;
    this.createChartForOutput(1);
    if (this.outputCount > 1) {
      this.createChartForOutput(2);
    } else {
      this.chartOut2?.destroy();
      this.chartOut2 = null;
    }
    window.setTimeout(() => {
      this.renderNowLinesOverlay();
      this.renderScheduleOverlay();
    }, 0);
    this.setupNowLineTimer();
  }

  onChartMouseMove(event: MouseEvent, output: 1 | 2): void {
    const chart = output === 1 ? this.chartOut1 : this.chartOut2;
    if (!chart) return;

    const canvas = chart.canvas as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const xScale = (chart as any).scales?.x;
    if (!xScale) return;

    const localX = event.clientX - rect.left;
    const weekMinute = Number(xScale.getValueForPixel(localX));
    if (!Number.isFinite(weekMinute)) return;

    const cycleRegions = output === 1 ? this.cycleRegionsOut1 : this.cycleRegionsOut2;
    const pulseRegions = output === 1 ? this.pulseRegionsOut1 : this.pulseRegionsOut2;
    const cycleHit = this.findCycleRegionAtWeekMinute(cycleRegions, weekMinute);
    const pulseHit = cycleHit ? null : this.findPulseRegionAtWeekMinute(pulseRegions, weekMinute);
    const nextHover = cycleHit
      ? { kind: 'cycle' as const, output, region: cycleHit }
      : pulseHit
        ? { kind: 'pulse' as const, output, region: pulseHit }
        : null;

    const hasMarkers = cycleRegions.length > 0 || pulseRegions.length > 0;

    if (!nextHover) {
      if (this.hoveredSchedule?.output === output) {
        this.hoveredSchedule = null;
        this.renderScheduleOverlay();
      }
      canvas.style.cursor = hasMarkers ? 'default' : '';
      return;
    }

    const sameHover =
      this.hoveredSchedule?.output === output &&
      this.hoveredSchedule.kind === nextHover.kind &&
      (
        (nextHover.kind === 'cycle' && this.hoveredSchedule.kind === 'cycle'
          && this.hoveredSchedule.region.weekMinuteStart === nextHover.region.weekMinuteStart)
        || (nextHover.kind === 'pulse' && this.hoveredSchedule.kind === 'pulse'
          && this.hoveredSchedule.region.weekMinuteStart === nextHover.region.weekMinuteStart)
      );

    if (!sameHover) {
      this.hoveredSchedule = nextHover;
      this.renderScheduleOverlay();
    }

    canvas.style.cursor = 'zoom-in';
  }

  onChartMouseLeave(output: 1 | 2): void {
    const chart = output === 1 ? this.chartOut1 : this.chartOut2;
    if (chart?.canvas) {
      (chart.canvas as HTMLCanvasElement).style.cursor = '';
    }

    if (this.hoveredSchedule?.output === output) {
      this.hoveredSchedule = null;
      this.renderScheduleOverlay();
    }
  }

  private setupNowLineTimer(): void {
    if (this.nowLineTimerId != null) {
      window.clearInterval(this.nowLineTimerId);
      this.nowLineTimerId = null;
    }

    this.nowLineTimerId = window.setInterval(() => {
      const r = this.getWindowRangeAroundNow();
      [this.chartOut1, this.chartOut2].forEach((c) => {
        if (!c) return;
        const xScale = (c.options.scales as any)?.x;
        if (xScale) {
          xScale.min = r.min;
          xScale.max = r.max;
        }
        c.update('none');
      });
      this.renderNowLinesOverlay();
      this.renderScheduleOverlay();
      this.validateDeviceClockAgainstSystem();
    }, 30_000);
  }

  private getReferenceNow(): Date {
    if (this.deviceClockBaseDate && this.deviceClockCapturedAtMs != null) {
      const elapsedMs = Math.max(0, Date.now() - this.deviceClockCapturedAtMs);
      return new Date(this.deviceClockBaseDate.getTime() + elapsedMs);
    }
    return new Date();
  }

  private getCurrentWeekMinute(): number {
    const now = this.getReferenceNow();
    const dayIndexMon0 = (now.getDay() + 6) % 7;
    return dayIndexMon0 * this.minutesPerDay + now.getHours() * 60 + now.getMinutes();
  }

  private formatCurrentClockTime(): string {
    const now = this.getReferenceNow();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  private syncDeviceClockReference(clock: any): void {
    const hour = Number(clock?.hour);
    const minute = Number(clock?.minute);
    const second = Number(clock?.seconds ?? 0);
    const day = Number(clock?.day);
    const month = Number(clock?.month);
    const yearRaw = Number(clock?.year);

    if ([hour, minute, second, day, month, yearRaw].some((value) => !Number.isFinite(value))) {
      this.deviceClockBaseDate = null;
      this.deviceClockCapturedAtMs = null;
      return;
    }

    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const baseDate = new Date(year, month - 1, day, hour, minute, second, 0);

    if (!Number.isFinite(baseDate.getTime())) {
      this.deviceClockBaseDate = null;
      this.deviceClockCapturedAtMs = null;
      return;
    }

    this.deviceClockBaseDate = baseDate;
    this.deviceClockCapturedAtMs = Date.now();
  }

  private getWindowRangeAroundNow(nowWeekMinute = this.getCurrentWeekMinute()): { min: number; max: number } {
    const extendedMin = -this.minutesPerDay;
    const extendedMax = (this.weekMinutes - 1) + this.minutesPerDay;
    const snap = Math.max(1, Math.trunc(this.nowSnapMinutes));
    const snappedNowWeekMinute = Math.floor(nowWeekMinute / snap) * snap;

    const minRaw = Math.max(extendedMin, snappedNowWeekMinute - this.windowBackMinutes);
    const maxRaw = Math.min(extendedMax, snappedNowWeekMinute + this.windowForwardMinutes);
    const step = 120;
    const min = Math.max(extendedMin, Math.floor(minRaw / step) * step);
    const max = Math.min(extendedMax, Math.ceil(maxRaw / step) * step);

    return { min, max: Math.max(min + 1, max) };
  }

  private renderNowLinesOverlay(): void {
    const chart1 = this.chartOut1;
    const chart2 = this.outputCount > 1 ? this.chartOut2 : null;
    if (!chart1 || (this.outputCount > 1 && !chart2)) return;
    if (!this.overlayContainer?.nativeElement || !this.nowLinesOverlay?.nativeElement) return;

    const containerEl = this.overlayContainer.nativeElement;
    const overlayCanvas = this.nowLinesOverlay.nativeElement;

    const containerRect = containerEl.getBoundingClientRect();
    const rect1 = (chart1.canvas as HTMLCanvasElement).getBoundingClientRect();
    const bottomChart = chart2 ?? chart1;
    const rectBottom = (bottomChart.canvas as HTMLCanvasElement).getBoundingClientRect();

    const widthCss = Math.max(1, Math.round(containerRect.width));
    const heightCss = Math.max(1, Math.round(containerRect.height));

    const dpr = window.devicePixelRatio || 1;
    overlayCanvas.width = Math.round(widthCss * dpr);
    overlayCanvas.height = Math.round(heightCss * dpr);

    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, widthCss, heightCss);

    const offsetX1 = rect1.left - containerRect.left;
    const offsetY1 = rect1.top - containerRect.top;
    const offsetYBottom = rectBottom.top - containerRect.top;

    const chartArea1 = (chart1 as any).chartArea;
    const chartAreaBottom = (bottomChart as any).chartArea;
    const xScale1 = (chart1 as any).scales?.x;
    if (!chartArea1 || !chartAreaBottom || !xScale1) return;

    const yTop = offsetY1 + chartArea1.top;
    const yBottom = offsetYBottom + chartAreaBottom.bottom;
    const overshoot = 36;
    const yLineTop = Math.max(0, yTop - overshoot);

    const drawRoundedRect = (x: number, y: number, w: number, h: number, r: number) => {
      const radius = Math.max(0, Math.min(r, w / 2, h / 2));
      const anyCtx = ctx as any;
      if (typeof anyCtx.roundRect === 'function') {
        anyCtx.roundRect(x, y, w, h, radius);
        return;
      }
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + w, y, x + w, y + h, radius);
      ctx.arcTo(x + w, y + h, x, y + h, radius);
      ctx.arcTo(x, y + h, x, y, radius);
      ctx.arcTo(x, y, x + w, y, radius);
      ctx.closePath();
    };

    const drawBubble = (xCenter: number, yCenter: number, text: string, bg: string, fg: string, arrowSize = 0) => {
      ctx.save();
      ctx.setLineDash([]);
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const paddingX = 10;
      const paddingY = 6;
      const textWidth = ctx.measureText(text).width;
      const w = textWidth + paddingX * 2;
      const h = 20 + paddingY;

      const minX = w / 2 + 2;
      const maxX = widthCss - w / 2 - 2;
      const x = Math.max(minX, Math.min(maxX, xCenter));

      const minY = h / 2;
      const extraBottom = arrowSize > 0 ? (arrowSize + 2) : 0;
      const maxY = heightCss - h / 2 - 2 - extraBottom;
      const y = Math.max(minY, Math.min(maxY, yCenter));

      ctx.fillStyle = bg;
      ctx.beginPath();
      drawRoundedRect(x - w / 2, y - h / 2, w, h, 10);
      ctx.fill();

      if (arrowSize > 0) {
        const baseY = y + h / 2;
        const tipY = Math.min(heightCss - 2, baseY + arrowSize);
        ctx.beginPath();
        ctx.moveTo(x, tipY);
        ctx.lineTo(x - arrowSize, baseY);
        ctx.lineTo(x + arrowSize, baseY);
        ctx.closePath();
        ctx.fill();
      }

      ctx.fillStyle = fg;
      ctx.fillText(text, x, y);
      ctx.restore();
    };

    const weekMinute = this.getCurrentWeekMinute();
    const xLocal = xScale1.getPixelForValue(weekMinute);
    const x = offsetX1 + xLocal;

    if (!Number.isFinite(x)) return;

    const primary = '#4267B2';
    const extraAboveBubbles = 16;
    const yStart = Math.max(0, yLineTop - extraAboveBubbles);

    ctx.save();
    ctx.strokeStyle = primary;
    ctx.globalAlpha = 0.65;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.moveTo(x, yStart);
    ctx.lineTo(x, yBottom);
    ctx.stroke();
    ctx.restore();

    drawBubble(x, yStart - 20, `🕒 ${this.formatCurrentClockTime()}`, primary, '#fff', 7);
  }

  private getHolidayNumericValue(value: any): number | null {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private parseHolidayTimeParts(hourValue: any, minuteValue?: any, hourText?: any): { h: number; m: number } | null {
    const numericHour = this.getHolidayNumericValue(hourValue);
    const numericMinute = this.getHolidayNumericValue(minuteValue);

    if (numericHour != null && numericMinute != null) {
      if (numericHour < 0 || numericHour > 23 || numericMinute < 0 || numericMinute > 59) return null;
      return { h: numericHour, m: numericMinute };
    }

    const text = typeof hourText === 'string' && hourText.includes(':')
      ? hourText
      : (typeof hourValue === 'string' && hourValue.includes(':') ? hourValue : null);

    if (!text) return null;

    const [hhRaw, mmRaw] = text.split(':');
    const h = Number(hhRaw);
    const m = Number(mmRaw);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return { h, m };
  }

  private isValidHolidayMonthDay(year: number, month: number, day: number): boolean {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false;
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === (month - 1) && date.getDate() === day;
  }

  private buildHolidayDate(year: number, month: number, day: number, time: { h: number; m: number }): Date | null {
    if (!this.isValidHolidayMonthDay(year, month, day)) return null;
    return new Date(year, month - 1, day, time.h, time.m, 0, 0);
  }

  private buildHolidayRangeForYear(period: any, year: number): { start: Date; end: Date; periodIndex: number } | null {
    if (!period || ('enable' in period && Number(period.enable) !== 1)) return null;

    const sd = Number(period.startDay);
    const sm = Number(period.startMonth);
    const ed = Number(period.endDay);
    const em = Number(period.endMonth);
    if ([sd, sm, ed, em].some((value) => !Number.isFinite(value))) return null;

    const startTime = this.parseHolidayTimeParts(period.startHour, period.startMinute, period.startHourStr);
    const endTime = this.parseHolidayTimeParts(period.endHour, period.endMinute, period.endHourStr);
    if (!startTime || !endTime) return null;

    const start = this.buildHolidayDate(year, sm, sd, startTime);
    if (!start) return null;

    let endYear = year;
    if (em < sm || (em === sm && (ed < sd || (ed === sd && (endTime.h < startTime.h || (endTime.h === startTime.h && endTime.m < startTime.m)))))) {
      endYear = year + 1;
    }

    const end = this.buildHolidayDate(endYear, em, ed, endTime);
    if (!end || end.getTime() < start.getTime()) return null;

    const periodIndex = Number(this.getHolidayNumericValue(period.period) ?? 0);
    return { start, end, periodIndex };
  }

  private getHolidayRangesAroundDate(period: any, referenceDate: Date): Array<{ start: Date; end: Date; periodIndex: number }> {
    const baseYear = referenceDate.getFullYear();
    return [
      this.buildHolidayRangeForYear(period, baseYear - 1),
      this.buildHolidayRangeForYear(period, baseYear),
      this.buildHolidayRangeForYear(period, baseYear + 1),
    ].filter((range): range is { start: Date; end: Date; periodIndex: number } => !!range);
  }

  private createHolidayPeriodPlugin(output: 1 | 2): any {
    return {
      id: 'holidayPeriodLine',
      afterDraw: (chart: any) => {
        if (output !== 1) return;

        const periods = this.holidayInfo?.periods;
        if (!Array.isArray(periods) || periods.length === 0) return;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;
        if (!xScale || !chartArea) return;

        const now = this.getReferenceNow();
        const currentWeekMinute = this.getCurrentWeekMinute();
        const minX = typeof xScale.min === 'number' ? xScale.min : 0;
        const maxX = typeof xScale.max === 'number' ? xScale.max : this.weekMinutes - 1;
        const windowStart = new Date(now.getTime() + (minX - currentWeekMinute) * 60_000);
        const windowEnd = new Date(now.getTime() + (maxX - currentWeekMinute) * 60_000);

        const rootStyle = getComputedStyle(document.documentElement);
        const bsPrimary = (rootStyle.getPropertyValue('--bs-primary') || rootStyle.getPropertyValue('--primary') || '').trim() || '#0d6efd';
        const bsSuccess = (rootStyle.getPropertyValue('--bs-success') || rootStyle.getPropertyValue('--success') || '').trim() || '#39b690';
        const bsDanger = (rootStyle.getPropertyValue('--bs-danger') || rootStyle.getPropertyValue('--danger') || '').trim() || '#dc3545';
        const bsSecondary = (rootStyle.getPropertyValue('--bs-secondary') || rootStyle.getPropertyValue('--secondary') || '').trim() || '#6c757d';
        const bsLight = (rootStyle.getPropertyValue('--bs-light') || rootStyle.getPropertyValue('--light') || '').trim() || '#f8f9fa';

        const colorForPeriod = (periodIndex: number): string => {
          if (periodIndex === 1) return bsPrimary;
          if (periodIndex === 2) return bsSuccess;
          if (periodIndex === 3) return bsDanger;
          if (periodIndex === 4) return bsSecondary;
          return bsLight;
        };

        const toRgba = (color: string, alpha: number): string => {
          const c = String(color || '').trim();
          const a = Math.max(0, Math.min(1, alpha));
          const rgbMatch = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i);
          if (rgbMatch) {
            return `rgba(${Number(rgbMatch[1])}, ${Number(rgbMatch[2])}, ${Number(rgbMatch[3])}, ${a})`;
          }
          const hexMatch = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
          if (hexMatch) {
            const hex = hexMatch[1];
            const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
            const r = parseInt(full.slice(0, 2), 16);
            const g = parseInt(full.slice(2, 4), 16);
            const b = parseInt(full.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${a})`;
          }
          return c;
        };

        const toRgb = (color: string): { r: number; g: number; b: number } | null => {
          const c = String(color || '').trim();
          const rgbMatch = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i);
          if (rgbMatch) {
            return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) };
          }
          const hexMatch = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
          if (!hexMatch) return null;
          const hex = hexMatch[1];
          const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
          return {
            r: parseInt(full.slice(0, 2), 16),
            g: parseInt(full.slice(2, 4), 16),
            b: parseInt(full.slice(4, 6), 16),
          };
        };

        const tintToRgba = (baseColor: string, tintToWhite: number, alpha: number): string => {
          const t = Math.max(0, Math.min(1, tintToWhite));
          const rgb = toRgb(baseColor);
          if (!rgb) return toRgba(baseColor, alpha);
          const r = Math.round(rgb.r * (1 - t) + 255 * t);
          const g = Math.round(rgb.g * (1 - t) + 255 * t);
          const b = Math.round(rgb.b * (1 - t) + 255 * t);
          return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
        };

        type VisibleLaneSegment = {
          startMinute: number;
          endMinute: number;
          periodIndex: number;
        };

        let drawnSegments = 0;
        const maxSegments = 8;
        const holidayLineWidth = 14;
        const y = Math.max(holidayLineWidth / 2 + 2, chartArea.top - 18);
        const laneLeft = chartArea.left;
        const laneRight = chartArea.right;

        ctx.save();
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        ctx.lineWidth = holidayLineWidth;

        const visibleSegments: VisibleLaneSegment[] = [];

        ctx.save();
        ctx.strokeStyle = toRgba(bsSecondary, 0.18);
        ctx.beginPath();
        ctx.moveTo(laneLeft, y);
        ctx.lineTo(laneRight, y);
        ctx.stroke();
        ctx.restore();

        for (const period of periods) {
          if (drawnSegments >= maxSegments) break;

          const ranges = this.getHolidayRangesAroundDate(period, now);
          for (const range of ranges) {
            if (drawnSegments >= maxSegments) break;

            const overlapStart = new Date(Math.max(range.start.getTime(), windowStart.getTime()));
            const overlapEnd = new Date(Math.min(range.end.getTime(), windowEnd.getTime()));
            if (overlapEnd.getTime() <= overlapStart.getTime()) continue;

            const xStart = currentWeekMinute + (overlapStart.getTime() - now.getTime()) / 60_000;
            const xEnd = currentWeekMinute + (overlapEnd.getTime() - now.getTime()) / 60_000;
            const pxStart = xScale.getPixelForValue(xStart);
            const pxEnd = xScale.getPixelForValue(xEnd);
            if (!Number.isFinite(pxStart) || !Number.isFinite(pxEnd)) continue;

            const segmentStart = Math.max(minX, xStart);
            const segmentEnd = Math.min(maxX, xEnd);
            if (segmentEnd <= segmentStart) continue;

            const baseColor = colorForPeriod(range.periodIndex);
            ctx.strokeStyle = tintToRgba(baseColor, 0.25, 0.95);
            ctx.beginPath();
            ctx.moveTo(pxStart, y);
            ctx.lineTo(pxEnd, y);
            ctx.stroke();

            visibleSegments.push({
              startMinute: segmentStart,
              endMinute: segmentEnd,
              periodIndex: range.periodIndex,
            });

            drawnSegments++;
          }
        }

        const normalizedSegments = [...visibleSegments]
          .sort((a, b) => a.startMinute - b.startMinute)
          .reduce((acc: VisibleLaneSegment[], segment) => {
            const last = acc[acc.length - 1];
            if (last && last.periodIndex === segment.periodIndex && segment.startMinute <= last.endMinute) {
              last.endMinute = Math.max(last.endMinute, segment.endMinute);
              return acc;
            }
            acc.push({ ...segment });
            return acc;
          }, []);

        const labeledSegments: VisibleLaneSegment[] = [];
        let cursor = minX;

        for (const segment of normalizedSegments) {
          const startMinute = Math.max(minX, segment.startMinute);
          const endMinute = Math.min(maxX, segment.endMinute);
          if (endMinute <= startMinute) continue;

          if (startMinute > cursor) {
            labeledSegments.push({
              startMinute: cursor,
              endMinute: startMinute,
              periodIndex: 0,
            });
          }

          labeledSegments.push({
            startMinute,
            endMinute,
            periodIndex: segment.periodIndex,
          });
          cursor = Math.max(cursor, endMinute);
        }

        if (cursor < maxX) {
          labeledSegments.push({
            startMinute: cursor,
            endMinute: maxX,
            periodIndex: 0,
          });
        }

        ctx.save();
        ctx.font = '600 11px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        for (const segment of labeledSegments) {
          const pxStart = xScale.getPixelForValue(segment.startMinute);
          const pxEnd = xScale.getPixelForValue(segment.endMinute);
          if (!Number.isFinite(pxStart) || !Number.isFinite(pxEnd)) continue;

          const segmentLeft = Math.max(laneLeft, Math.min(pxStart, pxEnd));
          const segmentRight = Math.min(laneRight, Math.max(pxStart, pxEnd));
          const segmentWidth = segmentRight - segmentLeft;

          let label = '';
          if (segment.periodIndex > 0) {
            const fullLabel = `${this.t('period')} ${segment.periodIndex}`;
            const shortLabel = `P${segment.periodIndex}`;
            const fullWidth = ctx.measureText(fullLabel).width;
            const shortWidth = ctx.measureText(shortLabel).width;

            if (fullWidth + 16 <= segmentWidth) {
              label = fullLabel;
            } else if (shortWidth + 10 <= segmentWidth) {
              label = shortLabel;
            }
          } else {
            const neutralLabel = this.t('dlNoActivePeriod');
            const neutralWidth = ctx.measureText(neutralLabel).width;
            if (segmentWidth >= 72 && neutralWidth + 16 <= segmentWidth) {
              label = neutralLabel;
            }
          }

          if (!label) continue;

          ctx.fillStyle = segment.periodIndex > 0 ? '#fff' : (bsSecondary || '#6c757d');
          ctx.fillText(label, segmentLeft + (segmentWidth / 2), y);
        }

        ctx.restore();
        ctx.restore();
      }
    };
  }

  private findCycleRegionAtWeekMinute(regions: DlCycleRegion[], weekMinute: number): DlCycleRegion | null {
    const padding = this.cycleHitPaddingMinutes;

    for (const region of regions) {
      if (
        weekMinute >= (region.weekMinuteStart - padding) &&
        weekMinute <= (region.weekMinuteEnd + padding)
      ) {
        return region;
      }
    }

    return null;
  }

  private findCycleRegionCoveringWeekMinute(regions: DlCycleRegion[], weekMinute: number): DlCycleRegion | null {
    for (const region of regions) {
      if (weekMinute >= region.weekMinuteStart && weekMinute < region.weekMinuteEnd) {
        return region;
      }
    }

    return null;
  }

  private pushChartLinePoint(points: Array<{ x: number; y: number }>, x: number, y: number): void {
    const last = points[points.length - 1];
    if (last && last.x === x && last.y === y) return;
    points.push({ x, y });
  }

  private displayXToChartIndex(displayX: number): number {
    if (displayX < 0) {
      return this.weekMinutes - this.minutesPerDay + (displayX + this.minutesPerDay);
    }
    if (displayX >= this.weekMinutes) {
      return displayX - this.weekMinutes;
    }
    return displayX;
  }

  /** Genera puntos de transición ON/OFF dentro de un tramo de ciclo. */
  private buildCycleWaveformPoints(region: DlCycleRegion): Array<{ x: number; y: 0 | 1 }> {
    const points: Array<{ x: number; y: 0 | 1 }> = [];
    const onSec = Math.max(1, region.onSeconds);
    const offSec = Math.max(1, region.offSeconds);
    const period = onSec + offSec;
    const phase0 = region.cyclePhaseOffsetSeconds || 0;
    const startSec = region.startSecondOfDay;
    const endSec = region.endSecondOfDay;
    const weekStartX = region.weekMinuteStart;

    const valueAtSec = (sec: number): 0 | 1 =>
      this._dlScheduleService.getCycleValueAtSecond(sec, region);

    let sec = startSec;
    points.push({ x: weekStartX, y: valueAtSec(sec) });

    while (sec < endSec) {
      const elapsed = (sec - startSec) + phase0;
      const phase = ((elapsed % period) + period) % period;
      const isOn = phase < onSec;
      const secToNext = isOn ? (onSec - phase) : (period - phase);
      const nextSec = Math.min(sec + Math.max(1, secToNext), endSec);
      sec = nextSec;
      if (sec >= endSec) break;
      points.push({ x: weekStartX + ((sec - startSec) / 60), y: valueAtSec(sec) });
    }

    points.push({ x: region.weekMinuteEnd, y: valueAtSec(Math.max(startSec, endSec - 1)) });
    return points;
  }

  private buildChartLinePoints(chartData: number[], cycleRegions: DlCycleRegion[]): Array<{ x: number; y: number }> {
    const points: Array<{ x: number; y: number }> = [];
    const xMin = -this.minutesPerDay;
    const xMax = this.weekMinutes + this.minutesPerDay - 1;
    const cycleMinuteSet = new Set<number>();

    cycleRegions.forEach((region) => {
      for (let wm = region.weekMinuteStart; wm < region.weekMinuteEnd; wm++) {
        cycleMinuteSet.add(wm);
      }
    });

    for (let x = xMin; x <= xMax; x++) {
      const chartIndex = this.displayXToChartIndex(x);
      if (cycleMinuteSet.has(chartIndex)) continue;
      this.pushChartLinePoint(points, x, (chartData[chartIndex] ?? 0) as 0 | 1);
    }

    const bufferOffsets = [-this.weekMinutes, 0, this.weekMinutes];
    cycleRegions.forEach((region) => {
      this.buildCycleWaveformPoints(region).forEach((point) => {
        bufferOffsets.forEach((offset) => {
          this.pushChartLinePoint(points, point.x + offset, point.y);
        });
      });
    });

    points.sort((a, b) => a.x - b.x);
    return points;
  }

  private drawCycleWaveformInChart(
    ctx: CanvasRenderingContext2D,
    region: DlCycleRegion,
    displayStart: number,
    displayEnd: number,
    xScale: any,
    yScale: any
  ): void {
    const onY = yScale.getPixelForValue(1);
    const offY = yScale.getPixelForValue(0);
    const leftPx = xScale.getPixelForValue(displayStart);
    const rightPx = xScale.getPixelForValue(displayEnd);
    if (!Number.isFinite(leftPx) || !Number.isFinite(rightPx)) return;

    const clipLeft = Math.min(leftPx, rightPx);
    const clipRight = Math.max(leftPx, rightPx);
    const topPx = Math.min(onY, offY);
    const bottomPx = Math.max(onY, offY);

    ctx.save();
    ctx.beginPath();
    ctx.rect(clipLeft, topPx, clipRight - clipLeft, bottomPx - topPx);
    ctx.clip();
    ctx.fillStyle = '#fff';
    ctx.fillRect(clipLeft, topPx, clipRight - clipLeft, bottomPx - topPx);

    const onSec = Math.max(1, region.onSeconds);
    const offSec = Math.max(1, region.offSeconds);
    const period = onSec + offSec;
    const phase0 = region.cyclePhaseOffsetSeconds || 0;
    const startSec = region.startSecondOfDay;
    const endSec = region.endSecondOfDay;
    const weekStartX = region.weekMinuteStart;
    const displayOffset = displayStart - weekStartX;

    const secToPx = (sec: number): number => {
      const displayX = weekStartX + ((sec - startSec) / 60) + displayOffset;
      return xScale.getPixelForValue(displayX);
    };

    let sec = startSec;
    let currentValue = this._dlScheduleService.getCycleValueAtSecond(sec, region);

    while (sec < endSec) {
      const elapsed = (sec - startSec) + phase0;
      const phase = ((elapsed % period) + period) % period;
      const isOn = phase < onSec;
      const secToNext = isOn ? (onSec - phase) : (period - phase);
      const nextSec = Math.min(sec + Math.max(1, secToNext), endSec);
      const segStartPx = secToPx(sec);
      const segEndPx = secToPx(nextSec);

      if (currentValue === 1 && segEndPx > segStartPx) {
        ctx.fillStyle = 'rgba(247, 210, 26, 0.12)';
        ctx.fillRect(segStartPx, onY, segEndPx - segStartPx, offY - onY);
        ctx.strokeStyle = '#f7d21a';
        ctx.lineWidth = 5;
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(segStartPx, onY);
        ctx.lineTo(segEndPx, onY);
        ctx.stroke();
      } else if (currentValue === 0 && segEndPx > segStartPx) {
        ctx.strokeStyle = '#6c757d';
        ctx.lineWidth = 5;
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(segStartPx, offY);
        ctx.lineTo(segEndPx, offY);
        ctx.stroke();
      }

      if (nextSec >= endSec) break;
      sec = nextSec;
      currentValue = this._dlScheduleService.getCycleValueAtSecond(sec, region);
    }

    ctx.restore();
  }

  private createCycleWaveformPlugin(output: 1 | 2): any {
    return {
      id: `cycleWaveform-${output}`,
      afterDatasetsDraw: (chart: any) => {
        const cycleRegions = output === 1 ? this.cycleRegionsOut1 : this.cycleRegionsOut2;
        if (!cycleRegions.length) return;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales?.x;
        const yScale = chart.scales?.y;
        if (!ctx || !chartArea || !xScale || !yScale) return;

        const xMin = Number(xScale.min);
        const xMax = Number(xScale.max);
        const bufferOffsets = [-this.weekMinutes, 0, this.weekMinutes];

        cycleRegions.forEach((region) => {
          bufferOffsets.forEach((offset) => {
            const displayStart = region.weekMinuteStart + offset;
            const displayEnd = region.weekMinuteEnd + offset;
            if (displayEnd < xMin || displayStart > xMax) return;

            this.drawCycleWaveformInChart(
              ctx,
              region,
              displayStart,
              displayEnd,
              xScale,
              yScale
            );
          });
        });
      },
    };
  }

  private findPulseRegionAtWeekMinute(regions: DlPulseRegion[], weekMinute: number): DlPulseRegion | null {
    const padding = this.pulseHitPaddingMinutes;

    for (const region of regions) {
      if (
        weekMinute >= (region.weekMinuteStart - padding) &&
        weekMinute <= (region.weekMinuteEnd + padding)
      ) {
        return region;
      }
    }

    return null;
  }

  private getChartValueAtWeekMinute(chartData: number[], weekMinute: number): number {
    const x = Math.trunc(weekMinute);

    if (x < 0) {
      const minute = ((x % this.minutesPerDay) + this.minutesPerDay) % this.minutesPerDay;
      return chartData[(this.weekMinutes - this.minutesPerDay) + minute] ?? 0;
    }

    if (x < this.weekMinutes) {
      return chartData[x] ?? 0;
    }

    const minute = x - this.weekMinutes;
    return chartData[minute] ?? 0;
  }

  private renderScheduleOverlay(): void {
    if (!this.overlayContainer?.nativeElement || !this.pulseZoomOverlay?.nativeElement) return;

    const containerEl = this.overlayContainer.nativeElement;
    const overlayCanvas = this.pulseZoomOverlay.nativeElement;
    const containerRect = containerEl.getBoundingClientRect();
    const widthCss = Math.max(1, Math.round(containerRect.width));
    const heightCss = Math.max(1, Math.round(containerRect.height));
    const dpr = window.devicePixelRatio || 1;

    overlayCanvas.width = Math.round(widthCss * dpr);
    overlayCanvas.height = Math.round(heightCss * dpr);

    const ctx = overlayCanvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, widthCss, heightCss);

    this.drawCycleMarkersForChart(ctx, containerRect, this.chartOut1, 1, this.cycleRegionsOut1);
    this.drawCycleMarkersForChart(ctx, containerRect, this.chartOut2, 2, this.cycleRegionsOut2);
    this.drawPulseMarkersForChart(ctx, containerRect, this.chartOut1, 1, this.pulseRegionsOut1);
    this.drawPulseMarkersForChart(ctx, containerRect, this.chartOut2, 2, this.pulseRegionsOut2);

    if (!this.hoveredSchedule) return;

    const { kind, output, region } = this.hoveredSchedule;
    const chart = output === 1 ? this.chartOut1 : this.chartOut2;
    if (!chart) return;

    const chartCanvas = chart.canvas as HTMLCanvasElement;
    const chartRect = chartCanvas.getBoundingClientRect();
    const chartArea = (chart as any).chartArea;
    const xScale = (chart as any).scales?.x;
    if (!chartArea || !xScale) return;

    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;

    const startX = offsetX + xScale.getPixelForValue(region.weekMinuteStart);
    const endX = offsetX + xScale.getPixelForValue(region.weekMinuteEnd);
    if (!Number.isFinite(startX) || !Number.isFinite(endX)) return;

    const bandLeft = Math.max(offsetX + chartArea.left, Math.min(startX, endX) - 4);
    const bandRight = Math.min(offsetX + chartArea.right, Math.max(startX, endX) + 4);
    const centerX = offsetX + xScale.getPixelForValue(region.centerWeekMinute);
    const magnifierWidth = 420;
    const magnifierHeight = 218;
    const accentColor = kind === 'cycle' ? '#2e8eaa' : '#f7d21a';
    let magnifierLeft = centerX - (magnifierWidth / 2);
    magnifierLeft = Math.max(8, Math.min(widthCss - magnifierWidth - 8, magnifierLeft));

    const spaceAbove = offsetY + chartArea.top;
    const spaceBelow = heightCss - (offsetY + chartArea.bottom);
    const placement: 'above' | 'below' = spaceAbove >= magnifierHeight + 22 || spaceAbove >= spaceBelow
      ? 'above'
      : 'below';
    const magnifierTop = placement === 'above'
      ? Math.max(8, offsetY + chartArea.top - magnifierHeight - 16)
      : Math.min(heightCss - magnifierHeight - 8, offsetY + chartArea.bottom + 16);
    const anchorY = placement === 'above' ? offsetY + chartArea.top : offsetY + chartArea.bottom;

    this.drawHoverBandHighlight(
      ctx,
      bandLeft,
      offsetY + chartArea.top,
      Math.max(2, bandRight - bandLeft),
      chartArea.bottom - chartArea.top,
      accentColor
    );
    this.drawMagnifierConnector(
      ctx,
      centerX,
      anchorY,
      magnifierLeft,
      magnifierTop,
      magnifierWidth,
      magnifierHeight,
      placement,
      accentColor
    );

    if (kind === 'pulse') {
      this.drawPulseMagnifier(ctx, magnifierLeft, magnifierTop, magnifierWidth, magnifierHeight, region);
      return;
    }

    this.drawCycleMagnifier(ctx, magnifierLeft, magnifierTop, magnifierWidth, magnifierHeight, region);
  }

  private drawHoverBandHighlight(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    width: number,
    height: number,
    accentColor: string
  ): void {
    ctx.save();
    ctx.fillStyle = accentColor === '#2e8eaa' ? 'rgba(46, 142, 170, 0.14)' : 'rgba(247, 210, 26, 0.16)';
    this.drawRoundedRect(ctx, left, top, width, height, 6);
    ctx.fill();
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 2;
    this.drawRoundedRect(ctx, left + 1, top + 1, width - 2, height - 2, 5);
    ctx.stroke();
    ctx.restore();
  }

  private drawMagnifierConnector(
    ctx: CanvasRenderingContext2D,
    anchorX: number,
    anchorY: number,
    magnifierLeft: number,
    magnifierTop: number,
    magnifierWidth: number,
    magnifierHeight: number,
    placement: 'above' | 'below',
    accentColor: string
  ): void {
    const tipX = Math.max(magnifierLeft + 18, Math.min(magnifierLeft + magnifierWidth - 18, anchorX));
    const cardEdgeY = placement === 'above' ? magnifierTop + magnifierHeight : magnifierTop;

    ctx.save();
    ctx.fillStyle = accentColor;
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.moveTo(anchorX - 8, anchorY);
    ctx.lineTo(anchorX + 8, anchorY);
    ctx.lineTo(tipX + 7, cardEdgeY);
    ctx.lineTo(tipX - 7, cardEdgeY);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawMagnifierShell(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    width: number,
    height: number,
    header: { accent: string; title: string; subtitle: string; titleX?: number; subtitleX?: number }
  ): { plotTop: number; plotHeight: number; plotLeft: number; plotWidth: number; axisY: number } {
    const radius = 12;
    const headerHeight = 54;
    const axisHeight = 30;
    const plotPadding = 16;
    const plotTop = top + headerHeight + 6;
    const plotHeight = height - headerHeight - axisHeight - 10;
    const plotLeft = left + plotPadding;
    const plotWidth = width - plotPadding * 2;
    const axisY = plotTop + plotHeight + 6;

    ctx.save();
    ctx.shadowColor = 'rgba(1, 30, 65, 0.18)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 4;
    this.drawRoundedRect(ctx, left, top, width, height, radius);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(1, 30, 65, 0.14)';
    ctx.lineWidth = 1;
    this.drawRoundedRect(ctx, left + 0.5, top + 0.5, width - 1, height - 1, radius);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = header.accent;
    ctx.fillRect(left + 12, top + 14, 4, 26);
    ctx.fillStyle = 'rgba(1, 30, 65, 0.08)';
    ctx.fillRect(left, top + headerHeight - 1, width, 1);
    ctx.restore();

    const titleX = left + (header.titleX ?? 24);
    const subtitleX = left + (header.subtitleX ?? 24);

    ctx.save();
    ctx.fillStyle = '#011e41';
    ctx.font = '700 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(header.title, titleX, top + 12);
    ctx.fillStyle = '#2e8eaa';
    ctx.font = '500 11px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(header.subtitle, subtitleX, top + 32);
    ctx.restore();

    return { plotTop, plotHeight, plotLeft, plotWidth, axisY };
  }

  private drawPlotLanes(
    ctx: CanvasRenderingContext2D,
    plotLeft: number,
    plotTop: number,
    plotWidth: number,
    plotHeight: number
  ): { onY: number; offY: number; midY: number } {
    const innerTop = plotTop + 4;
    const innerHeight = plotHeight - 8;
    const laneHeight = innerHeight / 2;
    const onY = innerTop + laneHeight * 0.32;
    const offY = innerTop + laneHeight + laneHeight * 0.68;
    const midY = innerTop + laneHeight;

    ctx.save();
    ctx.fillStyle = '#fff';
    this.drawRoundedRect(ctx, plotLeft, innerTop, plotWidth, innerHeight, 8);
    ctx.fill();
    ctx.restore();

    const drawLanePill = (text: string, y: number, bg: string, fg: string): void => {
      ctx.save();
      ctx.font = '700 9px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      const textWidth = ctx.measureText(text).width;
      const pillWidth = textWidth + 12;
      const pillX = plotLeft + plotWidth - pillWidth - 6;
      ctx.fillStyle = bg;
      this.drawRoundedRect(ctx, pillX, y - 8, pillWidth, 16, 8);
      ctx.fill();
      ctx.fillStyle = fg;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, pillX + pillWidth / 2, y);
      ctx.restore();
    };

    drawLanePill(this.t('on'), innerTop + 4, 'rgba(247, 210, 26, 0.22)', '#011e41');
    drawLanePill(this.t('off'), innerTop + innerHeight - 4, 'rgba(108, 117, 125, 0.12)', '#495057');

    return { onY, offY, midY };
  }

  private drawStepWaveform(
    ctx: CanvasRenderingContext2D,
    points: Array<{ x: number; value: 0 | 1 }>,
    onY: number,
    offY: number
  ): void {
    if (points.length < 2) return;

    const mapped = points.map((point) => ({
      x: point.x,
      y: point.value ? onY : offY,
      value: point.value,
    }));

    ctx.save();
    ctx.lineWidth = 3;
    ctx.lineJoin = 'miter';
    ctx.lineCap = 'butt';

    for (let index = 1; index < mapped.length; index++) {
      const prev = mapped[index - 1];
      const current = mapped[index];

      if (current.x > prev.x) {
        ctx.strokeStyle = prev.value ? '#f7d21a' : '#6c757d';
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(current.x, prev.y);
        ctx.stroke();
      }

      if (current.y !== prev.y) {
        ctx.strokeStyle = '#f7d21a';
        ctx.beginPath();
        ctx.moveTo(current.x, prev.y);
        ctx.lineTo(current.x, current.y);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private drawCycleTimelineBars(
    ctx: CanvasRenderingContext2D,
    region: DlCycleRegion,
    plotLeft: number,
    plotWidth: number,
    innerTop: number,
    innerHeight: number
  ): void {
    const regionStart = region.startSecondOfDay;
    const regionEnd = region.endSecondOfDay;
    const spanSec = Math.max(1, regionEnd - regionStart);

    ctx.save();
    for (let px = 0; px < Math.ceil(plotWidth); px++) {
      const second = regionStart + ((px + 0.5) / plotWidth) * spanSec;
      const isOn = this._dlScheduleService.getCycleValueAtSecond(second, region) === 1;

      if (!isOn) {
        continue;
      }

      ctx.fillStyle = 'rgba(247, 210, 26, 0.92)';
      ctx.fillRect(plotLeft + px, innerTop, 1, innerHeight);
    }
    ctx.restore();
  }

  private drawAxisTicks(
    ctx: CanvasRenderingContext2D,
    ticks: Array<{ x: number; label: string; strong?: boolean; sub?: string }>,
    plotTop: number,
    plotHeight: number,
    axisY: number,
    accentColor: string
  ): void {
    ticks.forEach((tick) => {
      ctx.save();
      ctx.strokeStyle = tick.strong ? accentColor : 'rgba(108, 117, 125, 0.35)';
      ctx.globalAlpha = tick.strong ? 0.95 : 0.65;
      ctx.lineWidth = tick.strong ? 1.5 : 1;
      ctx.beginPath();
      ctx.moveTo(tick.x, plotTop + plotHeight - 4);
      ctx.lineTo(tick.x, axisY + 4);
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = tick.strong ? '#011e41' : '#6c757d';
      ctx.font = tick.strong
        ? '700 11px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        : '500 10px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText(tick.label, tick.x, axisY + 8);

      if (tick.sub) {
        ctx.fillStyle = '#2e8eaa';
        ctx.font = '600 9px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillText(tick.sub, tick.x, axisY + 22);
      }

      ctx.restore();
    });
  }

  private preloadPulseIcon(): void {
    const image = new Image();
    image.src = 'assets/img/orbis/pulse-icon.svg';
    image.onload = () => {
      this.pulseIconImage = image;
      this.renderScheduleOverlay();
    };
    image.onerror = () => {
      this.pulseIconImage = null;
    };
  }

  private preloadPulseIconDark(): void {
    const image = new Image();
    image.src = 'assets/img/orbis/pulse-icon-dark.svg';
    image.onload = () => {
      this.pulseIconDarkImage = image;
      this.renderScheduleOverlay();
    };
    image.onerror = () => {
      this.pulseIconDarkImage = null;
    };
  }

  private preloadCycleIcon(): void {
    const image = new Image();
    image.src = 'assets/img/orbis/cycle-icon.svg';
    image.onload = () => {
      this.cycleIconImage = image;
      this.renderScheduleOverlay();
    };
    image.onerror = () => {
      this.cycleIconImage = null;
    };
  }

  private readonly cycleIconPath =
    'M280-80 120-240l160-160 56 58-62 62h406v-160h80v240H274l62 62-56 58Zm-80-440v-240h486l-62-62 56-58 160 160-160 160-56-58 62-62H280v160h-80Z';

  private drawCycleIcon(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    color: string
  ): void {
    if (color === '#fff' && this.cycleIconImage?.complete && this.cycleIconImage.naturalWidth > 0) {
      ctx.drawImage(this.cycleIconImage, cx - (size / 2), cy - (size / 2), size, size);
      return;
    }

    const viewSize = 960;
    ctx.save();
    ctx.translate(cx - (size / 2), cy - (size / 2));
    ctx.scale(size / viewSize, size / viewSize);
    ctx.translate(0, viewSize);
    ctx.fillStyle = color;
    ctx.fill(new Path2D(this.cycleIconPath));
    ctx.strokeStyle = color;
    ctx.lineWidth = size * 0.06;
    ctx.lineJoin = 'round';
    ctx.stroke(new Path2D(this.cycleIconPath));
    ctx.restore();
  }

  private drawPulseWaveIcon(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    width: number,
    height: number,
    color: string
  ): void {
    const iconImage = color === '#fff'
      ? this.pulseIconImage
      : color === '#011e41'
        ? this.pulseIconDarkImage
        : null;

    if (iconImage?.complete && iconImage.naturalWidth > 0) {
      ctx.drawImage(iconImage, cx - (width / 2), cy - (height / 2), width, height);
      return;
    }

    const left = cx - (width / 2);
    const top = cy - (height / 2);
    const scaleX = width / 24;
    const scaleY = height / 16;
    const px = (x: number): number => left + (x * scaleX);
    const py = (y: number): number => top + (y * scaleY);

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.6, height * 0.22);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(px(2), py(13));
    ctx.lineTo(px(7), py(13));
    ctx.lineTo(px(7), py(5));
    ctx.lineTo(px(17), py(5));
    ctx.lineTo(px(17), py(13));
    ctx.lineTo(px(22), py(13));
    ctx.stroke();
    ctx.restore();
  }

  private drawPulseMarkersForChart(
    ctx: CanvasRenderingContext2D,
    containerRect: DOMRect,
    chart: Chart | null,
    output: 1 | 2,
    regions: DlPulseRegion[]
  ): void {
    if (!chart || !regions.length) return;

    const chartCanvas = chart.canvas as HTMLCanvasElement;
    const chartRect = chartCanvas.getBoundingClientRect();
    const chartArea = (chart as any).chartArea;
    const xScale = (chart as any).scales?.x;
    if (!chartArea || !xScale) return;

    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;
    const minX = Number(xScale.min);
    const maxX = Number(xScale.max);

    regions.forEach((region) => {
      if (region.weekMinuteEnd < minX || region.weekMinuteStart > maxX) return;

      const cx = offsetX + xScale.getPixelForValue(region.centerWeekMinute);
      if (!Number.isFinite(cx)) return;
      if (cx < offsetX + chartArea.left - 14 || cx > offsetX + chartArea.right + 14) return;

      const iconRadius = 13;
      const cy = offsetY + chartArea.top + iconRadius + 8;
      const isHovered =
        this.hoveredSchedule?.kind === 'pulse' &&
        this.hoveredSchedule.output === output &&
        this.hoveredSchedule.region.weekMinuteStart === region.weekMinuteStart;

      ctx.save();
      ctx.fillStyle = isHovered ? '#2e8eaa' : '#011e41';
      ctx.beginPath();
      ctx.arc(cx, cy, iconRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      this.drawPulseWaveIcon(ctx, cx, cy, 20, 13, '#fff');
      ctx.restore();
    });
  }

  private drawCycleMarkersForChart(
    ctx: CanvasRenderingContext2D,
    containerRect: DOMRect,
    chart: Chart | null,
    output: 1 | 2,
    regions: DlCycleRegion[]
  ): void {
    if (!chart || !regions.length) return;

    const chartCanvas = chart.canvas as HTMLCanvasElement;
    const chartRect = chartCanvas.getBoundingClientRect();
    const chartArea = (chart as any).chartArea;
    const xScale = (chart as any).scales?.x;
    if (!chartArea || !xScale) return;

    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;
    const minX = Number(xScale.min);
    const maxX = Number(xScale.max);

    regions.forEach((region) => {
      if (region.weekMinuteEnd < minX || region.weekMinuteStart > maxX) return;

      const startPx = offsetX + xScale.getPixelForValue(region.weekMinuteStart);
      const endPx = offsetX + xScale.getPixelForValue(region.weekMinuteEnd);
      if (!Number.isFinite(startPx) || !Number.isFinite(endPx)) return;

      const left = Math.max(offsetX + chartArea.left, startPx);
      const right = Math.min(offsetX + chartArea.right, endPx);
      const width = right - left;
      const iconRadius = 18;
      if (width < iconRadius * 2 + 4) return;

      const cx = left + (width / 2);
      const cy = offsetY + ((chartArea.top + chartArea.bottom) / 2);
      const isHovered =
        this.hoveredSchedule?.kind === 'cycle' &&
        this.hoveredSchedule.output === output &&
        this.hoveredSchedule.region.weekMinuteStart === region.weekMinuteStart;

      ctx.save();
      ctx.fillStyle = isHovered ? '#2e8eaa' : '#011e41';
      ctx.beginPath();
      ctx.arc(cx, cy, iconRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#fff';
      this.drawCycleIcon(ctx, cx, cy, 20, '#fff');
      ctx.restore();
    });
  }

  private drawCycleMagnifier(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    width: number,
    height: number,
    region: DlCycleRegion
  ): void {
    const layout = this.drawMagnifierShell(ctx, left, top, width, height, {
      accent: '#2e8eaa',
      title: this.t('programKind_cycle'),
      subtitle: `${region.dayLabel} ${region.timeLabel} → ${region.endTimeLabel} · ${this.t('on')} ${this._dlScheduleService.formatDurationLabel(region.onSeconds)} / ${this.t('off')} ${this._dlScheduleService.formatDurationLabel(region.offSeconds)}`,
      titleX: 44,
      subtitleX: 24,
    });
    const { plotTop, plotHeight, plotLeft, plotWidth, axisY } = layout;
    const innerTop = plotTop + 4;
    const innerHeight = plotHeight - 8;
    const spanSec = Math.max(1, region.endSecondOfDay - region.startSecondOfDay);
    this.drawPlotLanes(ctx, plotLeft, plotTop, plotWidth, plotHeight);

    const secToPx = (second: number): number =>
      plotLeft + ((second - region.startSecondOfDay) / spanSec) * plotWidth;

    this.drawCycleTimelineBars(ctx, region, plotLeft, plotWidth, innerTop, innerHeight);

    this.drawAxisTicks(
      ctx,
      [
        { x: secToPx(region.startSecondOfDay), label: region.timeLabel, strong: true },
        { x: secToPx(region.endSecondOfDay), label: region.endTimeLabel, strong: true },
      ],
      plotTop,
      plotHeight,
      axisY,
      '#2e8eaa'
    );

    this.drawCycleIcon(ctx, left + 30, top + 19, 14, '#011e41');
  }


  private getPulseContextSeconds(durationSeconds: number): number {
    const duration = Math.max(1, durationSeconds || 1);
    return Math.min(15, Math.max(4, Math.ceil(duration * 4)));
  }

  private getPulseZoomWindowSeconds(region: DlPulseRegion): {
    startSec: number;
    endSec: number;
    pulseStartSec: number;
    pulseEndSec: number;
  } {
    const duration = Math.max(1, region.durationSeconds || 1);
    const pulseStartSec = region.startSecondOfDay;
    const pulseEndSec = pulseStartSec + duration;
    const padding = this.getPulseContextSeconds(duration);

    return {
      startSec: Math.max(0, pulseStartSec - padding),
      endSec: Math.min(86399, pulseEndSec + padding),
      pulseStartSec,
      pulseEndSec,
    };
  }

  private getPulseZoomValueAtSecond(secondOfDay: number, region: DlPulseRegion): 0 | 1 {
    const pulseStart = region.startSecondOfDay;
    const pulseEnd = pulseStart + Math.max(1, region.durationSeconds || 1);

    if (secondOfDay >= pulseStart && secondOfDay < pulseEnd) {
      return 1;
    }

    return 0;
  }

  private buildPulseZoomPoints(
    region: DlPulseRegion,
    zoomWindow: { startSec: number; endSec: number; pulseStartSec: number; pulseEndSec: number },
    secToPx: (second: number) => number
  ): Array<{ x: number; value: 0 | 1 }> {
    const spanSec = Math.max(1, zoomWindow.endSec - zoomWindow.startSec);
    const stepSec = spanSec > 3600 ? 60 : spanSec > 600 ? 10 : 1;
    const sampleSeconds = new Set<number>([
      zoomWindow.startSec,
      zoomWindow.endSec,
      zoomWindow.pulseStartSec,
      zoomWindow.pulseEndSec,
    ]);

    for (let second = zoomWindow.startSec; second <= zoomWindow.endSec; second += stepSec) {
      sampleSeconds.add(second);
    }

    return Array.from(sampleSeconds)
      .sort((a, b) => a - b)
      .map((second) => ({
        x: secToPx(second),
        value: this.getPulseZoomValueAtSecond(second, region),
      }));
  }

  private drawPulseMagnifier(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    width: number,
    height: number,
    region: DlPulseRegion
  ): void {
    const duration = Math.max(1, region.durationSeconds || 1);
    const zoomWindow = this.getPulseZoomWindowSeconds(region);
    const spanSec = Math.max(1, zoomWindow.endSec - zoomWindow.startSec);
    const pulseEndLabel = this._dlScheduleService.formatSecondLabel(zoomWindow.pulseEndSec);
    const layout = this.drawMagnifierShell(ctx, left, top, width, height, {
      accent: '#2e8eaa',
      title: this.t('programKind_pulse'),
      subtitle: `${region.dayLabel} ${region.timeSecondLabel} → ${pulseEndLabel} · ${this._dlScheduleService.formatDurationLabel(duration)}`,
      titleX: 48,
      subtitleX: 24,
    });
    const { plotTop, plotHeight, plotLeft, plotWidth, axisY } = layout;
    const { onY, offY } = this.drawPlotLanes(ctx, plotLeft, plotTop, plotWidth, plotHeight);

    const secToPx = (second: number): number =>
      plotLeft + ((second - zoomWindow.startSec) / spanSec) * plotWidth;

    const points = this.buildPulseZoomPoints(region, zoomWindow, secToPx);

    this.drawStepWaveform(ctx, points, onY, offY);

    this.drawAxisTicks(
      ctx,
      [
        { x: secToPx(zoomWindow.pulseStartSec), label: region.timeSecondLabel, strong: true },
        { x: secToPx(zoomWindow.pulseEndSec), label: pulseEndLabel, strong: true },
      ],
      plotTop,
      plotHeight,
      axisY,
      '#2e8eaa'
    );

    this.drawPulseWaveIcon(ctx, left + 30, top + 19, 22, 14, '#011e41');
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    const anyCtx = ctx as any;

    ctx.beginPath();
    if (typeof anyCtx.roundRect === 'function') {
      anyCtx.roundRect(x, y, width, height, r);
      return;
    }

    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private createChartForOutput(output: 1 | 2): void {
    const canvasRef = output === 1 ? this.editCanvasOut1 : this.editCanvasOut2;
    if (!canvasRef?.nativeElement) return;

    if (output === 1) {
      this.chartOut1?.destroy();
      this.chartOut1 = null;
    } else {
      this.chartOut2?.destroy();
      this.chartOut2 = null;
    }

    const ctx = canvasRef.nativeElement.getContext('2d');
    if (!ctx) return;

    const chartData = output === 1 ? this.chartDataOut1 : this.chartDataOut2;
    const cycleRegions = output === 1 ? this.cycleRegionsOut1 : this.cycleRegionsOut2;
    const range = this.getWindowRangeAroundNow();
    const points = this.buildChartLinePoints(chartData, cycleRegions);

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          segment: {
            borderColor: (segmentCtx: any) => {
              const y0 = Number(segmentCtx?.p0?.parsed?.y ?? 0);
              const y1 = Number(segmentCtx?.p1?.parsed?.y ?? 0);
              return (y0 === 0 && y1 === 0) ? '#6c757d' : '#f7d21a';
            },
            backgroundColor: (segmentCtx: any) => {
              const y0 = Number(segmentCtx?.p0?.parsed?.y ?? 0);
              const y1 = Number(segmentCtx?.p1?.parsed?.y ?? 0);
              return (y0 === 0 && y1 === 0) ? 'transparent' : 'rgba(247, 210, 26, 0.1)';
            },
          },
          data: points,
          stepped: true,
          fill: true,
          backgroundColor: 'rgba(247, 210, 26, 0.1)',
          borderWidth: 5,
          pointRadius: 0,
          borderColor: '#f7d21a',
          spanGaps: true,
        }]
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: this.holidayInfo?.periods?.length ? 56 : 48 } },
        scales: {
          y: {
            min: 0,
            max: 1,
            ticks: { stepSize: 1, callback: (v) => (v === 1 ? this.t('on') : v === 0 ? this.t('off') : '') }
          },
          x: {
            type: 'linear',
            min: range.min,
            max: range.max,
            ticks: {
              autoSkip: false,
              stepSize: 120,
              maxRotation: 0,
              callback: (value: any) => {
                const x = Math.trunc(Number(value));
                if (!Number.isFinite(x)) return '';
                const minuteOfDay = ((x % this.minutesPerDay) + this.minutesPerDay) % this.minutesPerDay;
                const hour = Math.floor(minuteOfDay / 60);
                if (hour === 0) return '🕛';
                return `${String(hour).padStart(2, '0')}:00`;
              }
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => (ctx.parsed.y ? this.t('switched_on') : this.t('switched_off'))
            }
          }
        }
      },
      plugins: [{
        id: 'channelLabel',
        afterDraw: (chartRef: any) => {
          const chartCtx = chartRef.ctx;
          const yScale = chartRef.scales?.y;
          if (!chartCtx || !yScale) return;

          chartCtx.save();
          chartCtx.fillStyle = '#6c757d';
          chartCtx.globalAlpha = 0.65;
          chartCtx.font = '600 12px sans-serif';
          chartCtx.textAlign = 'center';
          chartCtx.textBaseline = 'middle';
          chartCtx.translate(yScale.left + 12, (yScale.top + yScale.bottom) / 2);
          chartCtx.rotate(-Math.PI / 2);
          chartCtx.fillText(`${this.t('channel')} ${output}`, 0, 0);
          chartCtx.restore();
        }
      }, this.createHolidayPeriodPlugin(output), this.createCycleWaveformPlugin(output)]
    });

    if (output === 1) this.chartOut1 = chart;
    else this.chartOut2 = chart;

    window.setTimeout(() => this.renderScheduleOverlay(), 0);
  }
}
